<template>
    <div class="module-staff-report">
      <h1 class="text-3xl font-bold mb-6 text-gray-800">Module/Staff Relationship Report</h1>
      <div class="mb-4 flex flex-wrap items-center space-x-4">
        <div class="flex flex-col">
          <select 
            v-model="selectedSchool" 
            @change="handleSchoolChange" 
            class="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
            :class="{ 'border-red-500': errors.school }"
          >
            <option value="">Select School</option>
            <option v-for="school in schools" :key="school.id" :value="school.shortName">
              {{ school.fullName }}
            </option>
          </select>
          <span v-if="errors.school" class="text-red-500 text-sm mt-1">{{ errors.school[0] }}</span>
        </div>
        <div class="flex flex-col">
          <select 
            v-model="selectedDepartment" 
            @change="handleDepartmentChange" 
            class="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
            :class="{ 'border-red-500': errors.department }"
          >
            <option value="">Select Department</option>
            <option v-for="dept in filteredDepartments" :key="dept.id" :value="dept.shortName">
              {{ dept.fullName }}
            </option>
          </select>
          <span v-if="errors.department" class="text-red-500 text-sm mt-1">{{ errors.department[0] }}</span>
        </div>
        <input
          v-model="searchQuery"
          @input="handleSearch"
          type="text"
          placeholder="Search"
          class="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
        />
        <!--<button
          @click="openAddModal"
          class="bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-dark transition duration-300"
        >
          Add Module/Staff Relationship
        </button>-->
      </div>
      <div v-if="loading" class="flex justify-center items-center h-64">
        <div class="spinner"></div>
      </div>
      <div v-else-if="error" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
        <strong class="font-bold">Error:</strong>
        <span class="block sm:inline">{{ error }}</span>
      </div>
      <div v-else-if="reportData.length > 0">
        <button 
          @click="exportToExcel"
          class="mb-4 bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600 transition duration-300"
        >
          Export to Excel
        </button>
        <table class="min-w-full bg-white border border-gray-300">
          <thead class="bg-gray-800 text-white">
            <tr>
              <th class="text-left py-3 px-4 uppercase font-semibold text-sm border border-gray-300">Module Code</th>
              <th class="text-left py-3 px-4 uppercase font-semibold text-sm border border-gray-300">Module Name</th>
              <th class="text-left py-3 px-4 uppercase font-semibold text-sm border border-gray-300">School</th>
              <th class="text-left py-3 px-4 uppercase font-semibold text-sm border border-gray-300">Department</th>
              <th class="text-left py-3 px-4 uppercase font-semibold text-sm border border-gray-300">Staff</th>
              <th class="text-left py-3 px-4 uppercase font-semibold text-sm border border-gray-300">Activity Count</th>
          <!--    <th class="text-left py-3 px-4 uppercase font-semibold text-sm border border-gray-300">Actions</th>-->
            </tr>
          </thead>
          <tbody class="text-gray-700">
            <tr v-for="item in reportData" :key="item.moduleCode">
              <td class="text-left py-3 px-4 border border-gray-300">{{ item.moduleCode }}</td>
              <td class="text-left py-3 px-4 border border-gray-300">{{ item.moduleName }}</td>
              <td class="text-left py-3 px-4 border border-gray-300">{{ item.school }}</td>
              <td class="text-left py-3 px-4 border border-gray-300">{{ item.department }}</td>
              <td class="text-left py-3 px-4 border border-gray-300">
                <ul>
                  <li v-for="staff in item.staff" :key="staff.staffId">
                    {{ staff ? staff.name : "" }} (ID: {{ staff ? staff.staffId : "" }})
                  </li>
                </ul>
              </td>
              <td class="text-left py-3 px-4 border border-gray-300">{{ item.activityCount }}</td>
             <!-- <td class="text-left py-3 px-4 border border-gray-300">
                <button @click="editRelationship(item)" class="text-blue-600 hover:text-blue-800 mr-2">Edit</button>
                <button @click="deleteRelationship(item)" class="text-red-600 hover:text-red-800">Delete</button>
              </td>-->
            </tr>
          </tbody>
        </table>
        <div class="mt-4 flex justify-between items-center">
          <div>
            <span class="text-sm text-gray-700">
              Showing {{ (currentPage - 1) * itemsPerPage + 1 }} to {{ Math.min(currentPage * itemsPerPage, totalCount) }} of {{ totalCount }} entries
            </span>
          </div>
          <div class="flex space-x-2">
            <button @click="prevPage" :disabled="currentPage === 1" class="px-3 py-1 bg-gray-200 text-gray-700 rounded-md disabled:opacity-50">
              Previous
            </button>
            <button v-for="page in displayedPages" :key="page" @click="goToPage(page)" :class="['px-3 py-1 rounded-md', currentPage === page ? 'bg-primary text-white' : 'bg-gray-200 text-gray-700']">
              {{ page }}
            </button>
            <button @click="nextPage" :disabled="currentPage === totalPages" class="px-3 py-1 bg-gray-200 text-gray-700 rounded-md disabled:opacity-50">
              Next
            </button>
          </div>
        </div>
      </div>
      <div v-else class="text-center py-4 text-gray-500">
        No data available. Please select a school, department, or enter a search term to view the report.
      </div>
  
      <!-- Add/Edit Module/Staff Relationship Modal -->
      <div v-if="showModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
        <div class="bg-white p-8 rounded-lg shadow-xl w-full max-w-md">
          <h2 class="text-2xl font-bold mb-4">{{ isEditing ? 'Edit' : 'Add' }} Module/Staff Relationship</h2>
          <form @submit.prevent="submitRelationship">
            <div class="mb-4">
              <label for="moduleCode" class="block text-sm font-medium text-gray-700">Module Code</label>
              <select id="moduleCode" v-model="currentRelationship.moduleCode" required class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50">
                <option v-for="module in availableModules" :key="module.id" :value="module.moduleCode">
                  {{ module.moduleCode }} - {{ module.name }}
                </option>
              </select>
            </div>
            <div class="mb-4">
              <label for="staffId" class="block text-sm font-medium text-gray-700">Staff</label>
              <select id="staffId" v-model="currentRelationship.staffId" required class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50">
                <option v-for="staff in availableStaff" :key="staff.staffId" :value="staff.staffId">
                  {{ staff ? staff.name : "" }} (ID: {{ staff ? staff.staffId : ""}})
                </option>
              </select>
            </div>
            <div class="flex justify-end space-x-2">
              <button type="button" @click="closeModal" class="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400">Cancel</button>
              <button type="submit" class="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark">{{ isEditing ? 'Update' : 'Add' }}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import { mapState } from 'vuex';
  import { reportsApi } from '@/api/reports';
  import { dataApi } from '@/api/dataManagement';
  
  export default {
    name: 'ModuleStaffReport',
    data() {
      return {
        selectedSchool: '',
        selectedDepartment: '',
        reportData: [],
        loading: false,
        error: null,
        currentPage: 1,
        itemsPerPage: 10,
        totalCount: 0,
        errors: {
          school: null,
          department: null
        },
        searchQuery: '',
        showModal: false,
        isEditing: false,
        currentRelationship: {
          moduleCode: '',
          staffId: ''
        }
      }
    },
    computed: {
      ...mapState({
        schools: state => state.schools,
        departments: state => state.departments,
        modules: state => state.modules,
        staff: state => state.staff,
        userRole: state => state.auth.userRole,
        userSchool: state => state.auth.userSchool,
        userDepartment: state => state.auth.userDepartment
      }),
      filteredDepartments() {
        if (!this.selectedSchool) return this.departments;
        return this.departments.filter(dept => dept.school === this.selectedSchool);
      },
      totalPages() {
        return Math.ceil(this.totalCount / this.itemsPerPage);
      },
      displayedPages() {
        const range = 2;
        let start = Math.max(1, this.currentPage - range);
        let end = Math.min(this.totalPages, this.currentPage + range);
  
        if (start === 1) {
          end = Math.min(5, this.totalPages);
        }
        if (end === this.totalPages) {
          start = Math.max(1, this.totalPages - 4);
        }
  
        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
      },
      availableModules() {
        return this.modules.filter(module => 
          (!this.selectedSchool || module.school === this.selectedSchool) &&
          (!this.selectedDepartment || module.department === this.selectedDepartment)
        );
      },
      availableStaff() {
        return this.staff;
      }
    },
    methods: {
      async fetchReport() {
        this.loading = true;
        this.error = null;
        this.errors = { school: null, department: null };
  
        try {
          const filters = {
            school: this.selectedSchool,
            department: this.selectedDepartment,
            search: this.searchQuery,
            page: this.currentPage,
            pageSize: this.itemsPerPage
          };
          const response = await reportsApi.getModuleStaffReport(filters);
          this.reportData = response.items;
          this.totalCount = response.totalCount;
        } catch (error) {
          console.error('Error fetching report:', error);
          if (error.response && error.response.data && error.response.data.errors) {
            this.errors = error.response.data.errors;
          } else {
            this.error = 'Failed to fetch report data. Please try again.';
          }
        } finally {
          this.loading = false;
        }
      },
      handleSchoolChange() {
        this.selectedDepartment = '';
        this.currentPage = 1;
        this.fetchReport();
      },
      handleDepartmentChange() {
        this.currentPage = 1;
        this.fetchReport();
      },
      handleSearch() {
        this.currentPage = 1;
        this.fetchReport();
      },
      nextPage() {
        if (this.currentPage < this.totalPages) {
          this.currentPage++;
          this.fetchReport();
        }
      },
      prevPage() {
        if (this.currentPage > 1) {
          this.currentPage--;
          this.fetchReport();
        }
      },
      goToPage(page) {
        if (page >= 1 && page <= this.totalPages) {
          this.currentPage = page;
          this.fetchReport();
        }
      },
      async exportToExcel() {
        try {
          this.loading = true;
          const filters = {
            school: this.selectedSchool,
            department: this.selectedDepartment,
            search: this.searchQuery
          };
          const blob = await reportsApi.exportModuleStaffReport(filters);
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', 'module_staff_report.xlsx');
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } catch (error) {
          console.error('Error exporting report:', error);
          this.error = 'Failed to export report. Please try again.';
        } finally {
          this.loading = false;
        }
      },
      openAddModal() {
        this.isEditing = false;
        this.currentRelationship = {
          moduleCode: '',
          staffId: ''
        };
        this.showModal = true;
      },
      editRelationship(item) {
        this.isEditing = true;
        this.currentRelationship = {
          moduleCode: item.moduleCode,
          staffId: item.staff[0]?.staffId || ''
        };
        this.showModal = true;
      },
      closeModal() {
        this.showModal = false;
        this.currentRelationship = {
          moduleCode: '',
          staffId: ''
        };
      },
      async submitRelationship() {
        try {
          this.loading = true;
          if (this.isEditing) {
            await dataApi.updateItem('activityStaff', this.currentRelationship.id, this.currentRelationship);
          } else {
            await dataApi.createItem('activityStaff', this.currentRelationship);
          }
          this.closeModal();
          this.fetchReport();
        } catch (error) {
          console.error('Error submitting relationship:', error);
          this.error = 'Failed to submit relationship. Please try again.';
        } finally {
          this.loading = false;
        }
      },
      async deleteRelationship(item) {
        if (confirm('Are you sure you want to delete this relationship?')) {
          try {
            this.loading = true;
            await dataApi.deleteItem('activityStaff', item.id);
            this.fetchReport();
          } catch (error) {
            console.error('Error deleting relationship:', error);
            this.error = 'Failed to delete relationship. Please try again.';
          } finally {
            this.loading = false;
          }
        }
      }
    },
    created() {
      // Set initial filters based on user role
      if (this.userRole === 'school_admin') {
        this.selectedSchool = this.userSchool;
      } else if (this.userRole === 'department_admin') {
        this.selectedSchool = this.departments.find(d => d.shortName === this.userDepartment)?.school;
        this.selectedDepartment = this.userDepartment;
      }
      // Fetch initial report
      this.fetchReport();
    }
  }
  </script>
  
  <style scoped>
  .spinner {
    border: 4px solid #f3f3f3;
    border-top: 4px solid #3498db;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  </style>
  
  